import { Mapper } from '../../../@harmony';
import { FirestoreEntity } from '../data/firestore.entity';

type VersionMappers<T> = [] | [...mappers: Mapper<unknown, unknown>[], last: Mapper<unknown, T>];

type GetVersionFn = (data: any) => number;

/** Get version from Firestore document */
export const getVersionFn: GetVersionFn = (data: any): number => {
  if ('_version' in data && typeof data['_version'] === 'number') {
    return data['_version'];
  }

  throw new Error(`'_version' property not found or not a 'number'.`);
};

/** Get version from Firestore entity */
export const getFirestoreEntityVersionFn: GetVersionFn = (data: FirestoreEntity<any>): number =>
  getVersionFn(data.data);

/**
 * Map entity between versions
 *
 * Note that this upgrades between entity versions, and will return
 * the latest schema version for a document.
 *
 * E.g. UserEntityV0 => UserEntityV1 => UserEntityV2
 *
 * First entry (`idx=0`) of `versionMappers` is to map between v0 and
 * v1. `idx=1` maps from v1 to v2, etc.
 * */
export class VersionMapper<To> implements Mapper<unknown, To> {
  constructor(
    private readonly getVersion: GetVersionFn,
    private readonly versionMappers: VersionMappers<To>,
  ) {}

  public map(from: unknown): To {
    let mappedFrom: any = from;

    for (let version = this.getVersion(from); version < this.versionMappers.length; version++) {
      mappedFrom = this.versionMappers[version].map(mappedFrom);
    }

    return mappedFrom;
  }
}

type TypeList = [any, any, ...any[]];
type First<T extends TypeList> = T[0];
type Last<T extends TypeList> = T extends [...infer _, infer L] ? L : never;

// Thanks: https://stackoverflow.com/questions/77638947/validate-a-chain-of-mappers-types
type ChainedMappers<T extends TypeList> = T extends [any, ...infer R] | [...infer L, any]
  ? { [I in keyof L]: Mapper<L[I], R[I & keyof R]> }
  : never;

export class ChainedMapper<T extends TypeList> implements Mapper<First<T>, Last<T>> {
  constructor(private mappers: ChainedMappers<T>) {}

  public map(from: First<T>): Last<T> {
    let mappedFrom: any = from;

    this.mappers.forEach((mapper) => {
      mappedFrom = mapper.map(mappedFrom);
    });

    return mappedFrom;
  }
}
